import React, { useState, useEffect, useRef, useContext } from 'react';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { StoreContext } from '../../store';

import './index.css';
import NeighborhoodsData from '../../data/neigborhood-description.json';

import { ReactComponent as Search } from '../../assets/search.svg';
import { ReactComponent as CircleIcon } from '../../assets/address-score/circle.svg';
import { ReactComponent as Gem } from '../../assets/address-score/gem.svg';
import { ReactComponent as Favorite } from '../../assets/favorite-outline.svg';
import { ReactComponent as Rating } from '../../assets/rating-bar.svg';
import Placeholder from '../../assets/address-score/placeholder.png';

import NeighborhoodBounds from '../../assets/neighborhood-bound.png';

import {
  getLifeStyleWording,
  getWalkIndexWording,
  getLimit,
  getLifeStyleScore,
  saveAddressToFavorites,
  saveAddressToRecentlyViewed,
  isObjectEmpty,
} from '../../services';

import { fetchPlaceDetails } from '../../api';

import Map from './map';
import Show from '../../utility/Show';
import Skeleton from '../Skeleton';
import DiscoverNeighborhood from '../Plugin';
import MapContainer from '../Map';

const GetScore = ({ fetchAddressData }) => {
  const { state, actions, dispatch, navigate } = useContext(StoreContext);
  const {
    currentAddress,
    currentDistance,
    walkindexScores,
    lifeStyleScores,
    isStandalone,
    scoreProperties,
    savedAddresses,
    isGoogleMapsLoaded,
    currentView,
    isLoading,
    isMapLoading,
    token,
    currentCity,
    listOfMarkers,
    listOfProperties,
  } = state;
  const [predictions, setPredictions] = useState([]);
  const [isMounted, setIsMounted] = useState(false);
  const inputRef = useRef();
  const [loadingScores, setLoadingScores] = useState(false);
  const [hasDescription, setHasDescription] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const { '*': encodedAddress } = useParams();
  const address = decodeURIComponent(encodedAddress).replace(/-/g, ' ');

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (isMounted) {
      const fetchData = async () => {
        const data = await actions.fetchAddressData(address);
        
        dispatch({ type: 'SET_SHOW_SEARCH_ADDRESS', payload: false});
        dispatch({ type: 'SET_SHOW_DASHBOARD', payload: false });
      };
      fetchData();
    }
  }, [isMounted]);

  const fetchLifeStyleScores = async (payload, signal) => {
    try {
      await actions.fetchLifeStyleScores(payload, 0, false, true);
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Fetch aborted');
      } else {
        console.error('Fetch error:', error);
      }
      throw error;
    }
  };

  const fetchWalkIndexRatings = async (payload, signal) => {
    try {
      await actions.fetchWalkIndexRatings(payload, 0, false, true);
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Fetch aborted');
      } else {
        console.error('Fetch error:', error);
      }
      throw error;
    }
  };

  useEffect(() => {
    if (!currentAddress) return;

    const payload = {
      search_type: 'Address',
      query: currentAddress.property,
      check_only: false
    }
    actions.handleUsageTracker('address', currentAddress.property, false, true);

    if (currentAddress) {

      const controller = new AbortController();
      const signal = controller.signal;
      const payload = { latitude: currentAddress.latitude, longitude: currentAddress.longitude };

      const fetchData = async () => {
        try {
          setLoadingScores(true);
          await Promise.all([
            fetchLifeStyleScores(payload, signal),
            fetchWalkIndexRatings(payload, signal)
          ]);
        } catch (error) {
          console.log(error)
        } finally {
          setLoadingScores(false);
        }
      };

      fetchData();

      return () => {
        controller.abort();
      };
    }
  }, [currentAddress]);

  useEffect(() =>{
    if (!currentAddress) return;

    const controller = new AbortController();
    const signal = controller.signal;
    const payload = { latitude: currentAddress.latitude, longitude: currentAddress.longitude };

    const fetchData = async () => {
      try {
        setLoadingScores(true);
        await Promise.all([
          fetchLifeStyleScores(payload, signal),
          fetchWalkIndexRatings(payload, signal)
        ]);
      } catch (error) {
        console.log(error)
      } finally {
        setLoadingScores(false);
      }
    };

    fetchData();

    return () => {
      controller.abort();
    };
  }, [currentDistance]);

  const isAddressSaved = () => {
    return savedAddresses?.some(
      (address) => address?.property === currentAddress?.property
    );
  };

  const handleSaveAddressToFavorites = () => {
    saveAddressToFavorites(
      isAddressSaved() ? 'remove' : 'save',
      currentAddress,
      savedAddresses,
      dispatch,
      enqueueSnackbar,
    );
  };

  const lifeStyleEssentialScores = () => {
    if (!lifeStyleScores[0] || !isMounted) return {};

    const categories = ['Food', 'Services', 'Shops', 'Transit', 'Leisure'];
    const essentialScores = {};

    categories.forEach((category) => {
      const type = category === 'Leisure' ? 'leisure' : category.toLowerCase();
      essentialScores[category] = getLifeStyleScore(
        lifeStyleScores[0][type],
        currentDistance,
        isStandalone
      );
    });

    return essentialScores;
  };

  const getOverallGemScore = () => {
    if (isObjectEmpty(lifeStyleScores?.[0]) || !lifeStyleScores[0]) return null;
    const scores = Object.values(lifeStyleEssentialScores());

    const counts = [0, 0, 0];
    scores.forEach((score) => {
      const roundedScore = Math.round(score);
      counts[roundedScore - 1]++;
    });

    if (counts[2] >= 1) return 3;
    if (counts[1] >= 1) return 2;
    return 1;
  }

  const getOverallFeelScore = () => {
    if (isObjectEmpty(walkindexScores?.[0]) || !walkindexScores?.[0]) return null;
    const { activities, beautyM, beautyN, comfort, noise } = walkindexScores?.[0];

    const scores = [
      parseFloat(activities),
      parseFloat(beautyM),
      parseFloat(beautyN),
      parseFloat(comfort),
      parseFloat(noise),
    ];

    const counts = [0, 0, 0];
    scores.forEach((score) => {
      const roundedScore = Math.round(score);
      counts[roundedScore - 1]++;
    });

    if (counts[2] >= 1) return 3;
    if (counts[1] >= 1) return 2;
    return 1;
  };

  const getWalkableDescription = (score, isWalkIndex) => {
    const limit = getLimit(currentDistance);

    if (score === 1) {
      return isWalkIndex
        ? 'Scarce presence of features <25%'
        : `Less than ${limit[0]} living essentials`;
    } else if (score === 2) {
      return isWalkIndex
        ? 'Moderate presence of features >25% <50%'
        : `${limit[0]} -  ${limit[1]} living essentials`;
    } else {
      return isWalkIndex
        ? 'High presence of features >50%'
        : `> ${limit[1]} living essentials`;
    }
  };

  const searchAddress = () => {
    if (isGoogleMapsLoaded) {
      const autocompleteService =
        new window.google.maps.places.AutocompleteService();
      if (autocompleteService && inputRef.current) {
        const inputText = inputRef.current.value;
        const city = currentCity ? `${currentCity}, ` : '';

        autocompleteService.getPlacePredictions(
          {
            input: city + inputText,
            types: ['geocode'],
            componentRestrictions: { country: "us" }
          },
          async (predictions, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
              try {
                // Fetch details for each prediction
                const detailedPredictions = await Promise.all(predictions.map(async (prediction) => {
                  try {
                    const details = await fetchPlaceDetails(prediction.place_id);
                    return {
                      ...prediction,
                      latitude: details.geometry.location.lat(),
                      longitude: details.geometry.location.lng(),
                    };
                  } catch (error) {
                    console.error(`Failed to fetch details for place_id ${prediction.place_id}:`, error);
                    return null;
                  }
                }));

                // Filter out any null results (in case some place details couldn't be fetched)
                const validPredictions = detailedPredictions.filter(prediction => prediction !== null);
                setPredictions(validPredictions);
              } catch (error) {
                console.error('Error fetching place details:', error);
                setPredictions([]);
              }
            } else {
              setPredictions([]);
            }
          }
        );
      }
    }
  };

  const replaceWithDashes = (str) => {
    // Replace non-alphanumeric characters with dashes
    let result = str.replace(/[^a-zA-Z0-9]/g, '-');
    // Replace multiple consecutive dashes with a single dash
    result = result.replace(/-+/g, '-');
    // Remove leading and trailing dashes if any
    result = result.replace(/^-|-$/g, '');
    return result;
  };

  const addressClick = async (address) => {
    // dispatch({ type: 'SET_LOADING', payload: true });
    // setPredictions([]);
    // inputRef.current.value = '';
    // const data = {
    //   property: address.description,
    //   latitude: address.latitude,
    //   longitude: address.longitude
    // }
    // saveAddressToRecentlyViewed(data, recentlyViewedAddresses, dispatch);
    // await actions.fetchAddressData(address.description);
    const newAddress = `address/${replaceWithDashes(address.description)}`;
    const baseUrl = window.location.origin;
    window.location.replace(`${baseUrl}/${newAddress}`);
  };

  const handleSearchChange = (event) => {
    if (!event.target.value) {
      setPredictions([]);
    } else {
      searchAddress();
    }
  };

  const openScoreGuide = () => {
    dispatch({ type: 'SET_SHOW_SCORE_GUIDE', payload: true });
    dispatch({ type: 'SET_SHOW_TUTORIAL', payload: true });
  };

  const viewFullVersion = () => {
    const newAddress = `?address=${encodeURIComponent(currentAddress.property)}`;
    const baseUrl = window.location.origin;
    window.location.replace(`${baseUrl}/${newAddress}`);
  };

  const getNeighborhoodDescription = () => {
    if (!currentAddress?.sublocality) return null;

    const sublocalityData = NeighborhoodsData.sublocalities[currentAddress.sublocality];;
    if (!sublocalityData) return null;

    const selectedNeighborhood = sublocalityData.find(
      (neighborhood) =>
        currentAddress?.neighborhood?.toLowerCase() === neighborhood?.name?.toLowerCase()
    );

    if (selectedNeighborhood) {
      return (
        <div className='max-w-[90%] text-center'>
          <div className='text-[32px] font-medium mb-9'>{currentAddress.neighborhood}</div>
          {selectedNeighborhood.description
            .split('\n')
            .map((paragraph, index) => (
              <p key={index}>
                {paragraph.split('\n').map((line, index) => (
                  <React.Fragment key={index}>
                    {line}
                    <br />
                  </React.Fragment>
                ))}
              </p>
            ))}
        </div>
      );
    }
    return null;
  };

  const handleClick = (value) => {
    console.log('handleClick', value);
    // actions.fetchAddressData(payload.property);
  }

  return (
    <div className='address-score-body max-w-screen-lg justify-center'>
      <div className='search-address-input max-w-[500px] mx-auto'>
        <input
          ref={inputRef}
          onChange={(e) => handleSearchChange(e)}
          placeholder='Search for an address, location , cities ...'
        />
        <Search className='search-icon' />
        <Show>
          <Show.When isTrue={predictions.length !== 0}>
            <div
              className={`prediction-container ${
                currentView === 'Modern' && 'modern-prediction-container'
              }`}
            >
              {predictions.map((prediction) => (
                <div
                  className='prediction'
                  key={prediction.place_id}
                  onClick={() => addressClick(prediction)}
                >
                  {prediction.description}
                </div>
              ))}
            </div>
          </Show.When>
        </Show>
      </div>
      {getNeighborhoodDescription()}
      <DiscoverNeighborhood />
      <div className='relative h-[850px] w-full'>
        <MapContainer isFree={true} />
      </div>
    </div>
  )
};

export default GetScore;